<template>
  <Teleport to="body">
    <Transition name="slide">
      <div v-if="cartStore.isOpen" class="cart-sidebar">
        <div class="cart-sidebar__header">
          <h2 class="cart-sidebar__title">
            <ShoppingCart :size="22" />
            Your Cart
            <span v-if="cartStore.itemCount > 0" class="cart-sidebar__count">
              {{ cartStore.itemCount }}
            </span>
          </h2>
          <button class="cart-sidebar__close" @click="cartStore.toggleCart">
            <X :size="22" />
          </button>
        </div>

        <div v-if="cartStore.isEmpty" class="cart-sidebar__empty">
          <div class="empty-icon">
            <ShoppingBag :size="48" />
          </div>
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added anything to your cart yet.</p>
          <router-link to="/shop" class="btn btn-primary" @click="cartStore.toggleCart">
            <Package :size="18" />
            Start Shopping
          </router-link>
        </div>

        <template v-else-if="cartStore.items?.length > 0">
          <div class="cart-sidebar__items">
            <div 
              v-for="item in cartStore.items" 
              :key="item?.id || item?.cart_item_id || item?.product_id || Math.random()" 
              class="cart-item"
            >
              <img 
                :src="item?.image || '/placeholder.jpg'" 
                :alt="item?.name || 'Product'" 
                class="cart-item__image" 
              />
              <div class="cart-item__info">
                <h4 class="cart-item__name">{{ item?.name || 'Product' }}</h4>
                <p class="cart-item__price">
                  ${{ ((item?.unit_price || item?.price || 0)).toFixed(2) }}
                </p>
                <div class="cart-item__quantity">
                  <button @click="updateItemQuantity(item, (item?.quantity || 1) - 1)">
                    <Minus :size="14" />
                  </button>
                  <span>{{ item?.quantity || 1 }}</span>
                  <button @click="updateItemQuantity(item, (item?.quantity || 1) + 1)">
                    <Plus :size="14" />
                  </button>
                </div>
              </div>
              <button 
                class="cart-item__remove" 
                @click="removeCartItem(item)"
              >
                <Trash2 :size="18" />
              </button>
            </div>
          </div>

          <div class="cart-sidebar__footer">
            <div class="cart-sidebar__subtotal">
              <span>Subtotal</span>
              <span class="price">${{ cartStore.subtotal.toFixed(2) }}</span>
            </div>
            <div class="cart-sidebar__actions">
              <router-link to="/cart" class="btn btn-secondary" @click="cartStore.toggleCart">
                View Cart
              </router-link>
              <router-link to="/checkout" class="btn btn-primary" @click="cartStore.toggleCart">
                Checkout
                <ArrowRight :size="18" />
              </router-link>
            </div>
          </div>
        </template>
      </div>
    </Transition>
    <Transition name="fade">
      <div v-if="cartStore.isOpen" class="cart-overlay" @click="cartStore.toggleCart" />
    </Transition>
  </Teleport>
</template>

<script setup>
import { ShoppingCart, ShoppingBag, X, Minus, Plus, Trash2, Package, ArrowRight } from 'lucide-vue-next'
import { useCartStore } from '@/stores/cart'

const cartStore = useCartStore()

// Helper methods to handle item operations safely
const getItemId = (item) => {
  return item?.id || item?.cart_item_id || item?.product_id
}

const updateItemQuantity = (item, quantity) => {
  const itemId = getItemId(item)
  if (!itemId) {
    console.warn('Cannot update quantity: item has no ID')
    return
  }
  cartStore.updateQuantity(itemId, quantity)
}

const removeCartItem = (item) => {
  const itemId = getItemId(item)
  if (!itemId) {
    console.warn('Cannot remove item: item has no ID')
    return
  }
  cartStore.removeItem(itemId)
}
</script>

<style lang="scss" scoped>
$primary: #2563EB;
$primary-dark: #1D4ED8;
$accent-cyan: #06B6D4;
$danger: #dc3545;

.cart-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 420px;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.2);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, $primary 0%, $primary-dark 100%);
    color: white;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-family: 'Poppins', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 26px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50px;
    font-size: 0.8rem;
    padding: 0 0.5rem;
  }

  &__close {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: white;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      transform: rotate(90deg);
    }
  }

  &__empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;

    .empty-icon {
      width: 100px;
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, rgba($primary, 0.1), rgba($accent-cyan, 0.1));
      border-radius: 50%;
      color: $primary;
      margin-bottom: 1rem;
      animation: float 3s ease-in-out infinite;
    }

    h3 {
      font-family: 'Poppins', sans-serif;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
    }

    p {
      color: #666;
      font-size: 0.95rem;
    }
  }

  &__items {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
  }

  &__footer {
    padding: 1.5rem 2rem 2rem;
    background: linear-gradient(to top, white, rgba(255, 255, 255, 0.9));
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  &__subtotal {
    display: flex;
    justify-content: space-between;
    font-family: 'Poppins', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;

    .price {
      background: linear-gradient(90deg, $primary, $accent-cyan);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}

.cart-item {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 1rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateX(-4px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  &__image {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 12px;
    background: #f5f5f5;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__name {
    font-family: 'Poppins', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__price {
    font-weight: 700;
    color: $primary;
    font-size: 1rem;
  }

  &__quantity {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: #f5f5f5;
    border-radius: 10px;
    padding: 0.25rem;
    margin-top: 0.25rem;
    width: fit-content;

    button {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: linear-gradient(135deg, $primary, $primary-dark);
        color: white;
      }
    }

    span {
      width: 36px;
      text-align: center;
      font-weight: 600;
      color: #1a1a1a;
    }
  }

  &__remove {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff5f5;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    color: $danger;
    transition: all 0.2s;
    flex-shrink: 0;
    align-self: flex-start;

    &:hover {
      background: $danger;
      color: white;
      transform: scale(1.1);
    }
  }
}

.cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

// Animations
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

// Transitions
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// Cart item transitions
.cart-item-enter-active,
.cart-item-leave-active {
  transition: all 0.3s ease;
}

.cart-item-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.cart-item-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
